// Craft operations. See docs/04-equipment-craft §4.6 and
// docs/adr/0023-craft-operations.md.
//
// 9 craft operations, each a pure deterministic `Item -> Item` transform:
// the input `Item` is never mutated, a new one is returned, and the same
// item + same `Rng` position always yields the same result. The drop-pipeline
// stage functions (loot.ts) are reused for the affix / itemSkill / behaviorRule
// rolls. Material / currency costs are out of scope (docs/15 §D.4).
//
// 不正な操作 (rarity/category ゲート違反、set/unique 装備への操作、前提欠如) は
// 全て throw する。呼び出し側 (将来のクラフト UI / sim) が事前に可否を判定する。
//
// docs/04 §4.6 の「一時アフィックス / 劣化 / 一時的に超過」は Affix 型に時限・劣化
// フィールドが無く temporal モデルが未定義のため、本 ADR では「一時」性を落として
// 恒久変換として実装する (劣化・解除コストは別 ADR)。
import { ITEM_BASES } from './base-items.js';
import { AFFIX_DEFINITIONS } from './affixes.js';
import { rollAffix, rollAffixes, rollAffixValue, rollBehaviorRule, rollItemSkill } from './loot.js';
const ITEM_BASE_BY_ID = new Map(ITEM_BASES.map((base) => [base.id, base]));
const AFFIX_DEFINITION_BY_ID = new Map(AFFIX_DEFINITIONS.map((def) => [def.id, def]));
/**
 * rarity 別の affix 枠上限。生成定数 (rare 6 / set 4、ADR 0019 §4.1) と magic 2 から
 * 定式化。`craftEnchant` はこの上限を +1 まで超えて affix を付与できる。
 */
const AFFIX_SLOT_CAP = {
    normal: 0,
    magic: 2,
    rare: 6,
    set: 4,
    unique: 0,
};
// =============================================================================
// 共通ゲート / ヘルパー
// =============================================================================
/** set / unique 装備は catalogue 固定の特別装備のためクラフト不可。違反時 throw。 */
function assertCraftable(item, op) {
    if (item.rarity === 'set' || item.rarity === 'unique') {
        throw new Error(`${op}: set/unique 装備はクラフト不可 (rarity ${item.rarity})`);
    }
}
function requireBase(item, op) {
    const base = ITEM_BASE_BY_ID.get(item.baseId);
    if (base === undefined)
        throw new Error(`${op}: unknown baseId ${item.baseId}`);
    return base;
}
function requireAffixDef(affixId, op) {
    const def = AFFIX_DEFINITION_BY_ID.get(affixId);
    if (def === undefined)
        throw new Error(`${op}: unknown affixId ${affixId}`);
    return def;
}
// =============================================================================
// drop 段関数の再利用で機構が明快な 6 操作
// =============================================================================
/**
 * リフォージ: レア装備をベース維持で affix 全リロール (docs/04 §4.6)。
 * `rollAffixes` で affixes を再抽選する。baseId/slot/ilvl/itemSkills/behaviorRule/
 * sealed/id は保持。`sealed` は別バケットのため reforge を生き延びる (封印の意義)。
 */
export function craftReforge(item, rng) {
    assertCraftable(item, 'craftReforge');
    if (item.rarity !== 'rare') {
        throw new Error(`craftReforge: rare 装備のみ (rarity ${item.rarity})`);
    }
    const base = requireBase(item, 'craftReforge');
    return { ...item, affixes: rollAffixes(base, 'rare', item.ilvl, undefined, rng) };
}
/** 浄化: rng 選択した affix を 1 つ除去 (docs/04 §4.6)。affixes が空なら throw。 */
export function craftPurify(item, rng) {
    assertCraftable(item, 'craftPurify');
    if (item.affixes.length === 0) {
        throw new Error('craftPurify: 除去できる affix がない');
    }
    const index = rng.nextInt(item.affixes.length);
    return { ...item, affixes: item.affixes.filter((_, i) => i !== index) };
}
/**
 * Tier 昇格: rng 選択した affix の Tier を +1 し、新 Tier の range で値を再抽選
 * (docs/04 §4.6)。Tier 5 の affix は昇格対象から除く。昇格できる affix が
 * 無ければ throw。affix の `source` は維持する。
 */
export function craftPromoteTier(item, rng) {
    assertCraftable(item, 'craftPromoteTier');
    const promotable = item.affixes
        .map((affix, index) => ({ affix, index }))
        .filter((entry) => entry.affix.tier < 5);
    if (promotable.length === 0) {
        throw new Error('craftPromoteTier: Tier を上げられる affix がない');
    }
    const target = promotable[rng.nextInt(promotable.length)];
    const def = requireAffixDef(target.affix.id, 'craftPromoteTier');
    const newTier = (target.affix.tier + 1);
    const promoted = {
        ...target.affix,
        tier: newTier,
        values: [rollAffixValue(def.valuesByTier[newTier], rng)],
    };
    return {
        ...item,
        affixes: item.affixes.map((affix, i) => (i === target.index ? promoted : affix)),
    };
}
/**
 * トランスミュート: 通常装備を magic 化し magic affix を抽選 (docs/04 §4.6)。
 * normal 以外は throw。itemSkills は magic では 0 枠のため変化しない。
 */
export function craftTransmute(item, rng) {
    assertCraftable(item, 'craftTransmute');
    if (item.rarity !== 'normal') {
        throw new Error(`craftTransmute: normal 装備のみ (rarity ${item.rarity})`);
    }
    const base = requireBase(item, 'craftTransmute');
    return {
        ...item,
        rarity: 'magic',
        affixes: rollAffixes(base, 'magic', item.ilvl, undefined, rng),
    };
}
/**
 * 行動ルール再抽選: 部位の AI 行動ルールを再抽選 (docs/04 §4.6)。`rollBehaviorRule`
 * を再利用。カテゴリを持たない slot (weaponMain / offHand / belt / cloak) は throw。
 */
export function craftRerollBehaviorRule(item, rng) {
    assertCraftable(item, 'craftRerollBehaviorRule');
    const behaviorRule = rollBehaviorRule(item.slot, rng);
    if (behaviorRule === undefined) {
        throw new Error(`craftRerollBehaviorRule: slot ${item.slot} は行動ルールカテゴリを持たない`);
    }
    return { ...item, behaviorRule };
}
/**
 * スキル再ロール: レア装備の itemSkill 枠 1 つを再抽選 (docs/04 §4.6)。再ロール枠
 * 以外の itemSkill のテンプレートは除外し重複を防ぐ。rare 以外・itemSkill 無しは throw。
 */
export function craftRerollItemSkill(item, rng) {
    assertCraftable(item, 'craftRerollItemSkill');
    if (item.rarity !== 'rare') {
        throw new Error(`craftRerollItemSkill: rare 装備のみ (rarity ${item.rarity})`);
    }
    if (item.itemSkills.length === 0) {
        throw new Error('craftRerollItemSkill: 再ロールできる itemSkill がない');
    }
    const index = rng.nextInt(item.itemSkills.length);
    const exclude = new Set(item.itemSkills.filter((_, i) => i !== index).map((skill) => skill.templateId));
    const rerolled = rollItemSkill(item.ilvl, exclude, undefined, rng);
    if (rerolled === undefined) {
        throw new Error('craftRerollItemSkill: itemSkill プールが枯渇');
    }
    return {
        ...item,
        itemSkills: item.itemSkills.map((skill, i) => (i === index ? rerolled : skill)),
    };
}
// =============================================================================
// 追加モデルが要る 3 操作 (恒久変換として実装、§J / ADR 0023 §2)
// =============================================================================
/**
 * 封印: rng 選択した affix を `affixes` から `sealed[]` へ移す (docs/04 §4.6)。
 * `sealed` の affix は以降のクラフト (リフォージ等) が触れないロック枠として働く。
 * docs の「一時アフィックス / 劣化 / 解除コスト」は temporal モデル未定義のため
 * 別 ADR。本操作は恒久ロック変換。affixes が空なら throw。
 */
export function craftSeal(item, rng) {
    assertCraftable(item, 'craftSeal');
    if (item.affixes.length === 0) {
        throw new Error('craftSeal: 封印できる affix がない');
    }
    const index = rng.nextInt(item.affixes.length);
    const sealedAffix = item.affixes[index];
    return {
        ...item,
        affixes: item.affixes.filter((_, i) => i !== index),
        sealed: [...(item.sealed ?? []), sealedAffix],
    };
}
/**
 * エンチャント: affix 枠上限 (`AFFIX_SLOT_CAP`) を +1 まで超えて affix を 1 つ
 * 恒久付与 (docs/04 §4.6)。magic / rare のみ。affixes が cap を超えていれば
 * (= 既にエンチャント済み) throw。family 重複を避けて新 affix を抽選する。
 */
export function craftEnchant(item, rng) {
    assertCraftable(item, 'craftEnchant');
    if (item.rarity !== 'magic' && item.rarity !== 'rare') {
        throw new Error(`craftEnchant: magic / rare 装備のみ (rarity ${item.rarity})`);
    }
    const cap = AFFIX_SLOT_CAP[item.rarity];
    if (item.affixes.length > cap) {
        throw new Error(`craftEnchant: 既にエンチャント済み (affixes ${item.affixes.length} > cap ${cap})`);
    }
    const base = requireBase(item, 'craftEnchant');
    const usedFamilies = new Set();
    for (const affix of item.affixes) {
        usedFamilies.add(requireAffixDef(affix.id, 'craftEnchant').family);
    }
    // kind を 1 つ rng 抽選し、そのプールが family 除外後に枯渇していたら反対 kind へ
    // フォールバックする。`rollAffix` はプール空時に RNG を消費しないため決定論を保つ。
    const primaryKind = rng.nextBool() ? 'prefix' : 'suffix';
    const secondaryKind = primaryKind === 'prefix' ? 'suffix' : 'prefix';
    const enchanted = rollAffix(base, primaryKind, usedFamilies, item.ilvl, rng) ??
        rollAffix(base, secondaryKind, usedFamilies, item.ilvl, rng);
    if (enchanted === undefined) {
        throw new Error('craftEnchant: 付与可能な affix がない');
    }
    return { ...item, affixes: [...item.affixes, enchanted] };
}
/**
 * シャード適用: `AffixShard` の affix で既存 affix を上書き / 強化 (docs/04 §4.6)。
 * shard と同 family の affix があればそれを上書き (= 強化、family 一意性により
 * 高々 1 個)、無ければ同 kind の affix を rng 選択して上書き (= 上書き)。強化では
 * 既存 tier を下回らない (`max(既存, shard.tier)`、docs/04「ブリック確率は低め」)。
 * 上書きでは shard.tier をそのまま採用する。この規則で操作後も family 重複は発生
 * しない。上書きできる同 kind affix が無い、または shard の affix が item の
 * slot / baseKind に適用不可なら throw。
 */
export function craftApplyShard(item, shard, rng) {
    assertCraftable(item, 'craftApplyShard');
    const shardDef = requireAffixDef(shard.affixId, 'craftApplyShard');
    const base = requireBase(item, 'craftApplyShard');
    if (!shardDef.applicableSlots.includes(item.slot)) {
        throw new Error(`craftApplyShard: ${shard.affixId} は slot ${item.slot} に適用不可`);
    }
    if (shardDef.applicableBaseKinds !== undefined &&
        !shardDef.applicableBaseKinds.includes(base.baseKind)) {
        throw new Error(`craftApplyShard: ${shard.affixId} は baseKind ${base.baseKind} に適用不可`);
    }
    const candidates = item.affixes
        .map((affix, index) => ({ index, def: requireAffixDef(affix.id, 'craftApplyShard') }))
        .filter((entry) => entry.def.kind === shardDef.kind);
    if (candidates.length === 0) {
        throw new Error(`craftApplyShard: 上書き対象の ${shardDef.kind} affix がない`);
    }
    const sameFamily = candidates.find((entry) => entry.def.family === shardDef.family);
    const targetIndex = sameFamily !== undefined
        ? sameFamily.index
        : candidates[rng.nextInt(candidates.length)].index;
    // same-family (= 強化) は既存 tier を下回らない (docs/04 §4.6「ブリック確率は低め」)。
    // diff-family (= 上書き) は shard の tier をそのまま採用する。
    const appliedTier = sameFamily !== undefined
        ? Math.max(item.affixes[targetIndex].tier, shard.tier)
        : shard.tier;
    const applied = {
        id: shard.affixId,
        tier: appliedTier,
        values: [rollAffixValue(shardDef.valuesByTier[appliedTier], rng)],
        kind: shardDef.kind,
        source: 'rolled',
    };
    return {
        ...item,
        affixes: item.affixes.map((affix, i) => (i === targetIndex ? applied : affix)),
    };
}
//# sourceMappingURL=craft.js.map